import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Card, Row, Col, Input, InputNumber, Button, Table, Tabs,
    Space, Tag, Typography, Popconfirm, Divider, Checkbox,
    Modal, Select, Spin, Alert, Empty
} from 'antd';
import {
    ArrowLeftOutlined, SaveOutlined, PlusOutlined, CopyOutlined,
    DeleteOutlined, AppstoreOutlined, SearchOutlined, ReloadOutlined,
    CheckCircleOutlined
} from '@ant-design/icons';
import { getRequest, postRequest, putRequest } from 'api';
import { toast } from 'react-toastify';

const { Title } = Typography;
const { Option } = Select;

export interface RevenueBlockOption {
    id: number;
    code: string;
    name: string;
}

export interface IncomeSchool {
    id: number;
    code?: string;
    name: string;
    type: number;
    collectBy: number;
}

export interface QuickRegisterItem {
    id?: number;
    quickRegisterLevelId?: number;
    incomeSchoolId: number;
    incomeSchoolName?: string;
    incomeSchoolCode?: string;
    collectBy?: number;
    units: number;
    unitPrice: number;
    amount?: number;
    isDefault?: boolean;
}

export interface QuickRegisterLevel {
    id?: number;
    quickRegisterId?: number;
    name: string;
    totalAmount?: number;
    sortOrder?: number;
    items: QuickRegisterItem[];
}

export interface QuickRegister {
    id: number;
    schoolId: number;
    revenueBlockId: number;
    revenueBlockName?: string;
    month: number;
    year: number;
    name: string;
    status: number;
    appliedDate?: string;
    appliedBy?: string;
    createdDate?: string;
    levels: QuickRegisterLevel[];
}

interface QuickRegisterEditorProps {
    initialData?: QuickRegister | null;
    revenueBlockId: number;
    blockName: string;
    month: number;
    year: number;
    incomeSources: IncomeSchool[];
    blocks?: RevenueBlockOption[];
    onBack: () => void;
    onSaveSuccess: () => void;
}

export const COLLECT_BY_LABEL: Record<number, { text: string; color: string }> = {
    1: { text: 'Theo tháng', color: 'blue' },
    2: { text: 'Theo năm', color: 'purple' },
    3: { text: 'Suất-tháng', color: 'orange' },
    4: { text: 'Theo kỳ', color: 'cyan' },
};

// Helper: Kiểm tra hình thức thu có cố định số lượng = 1 không (theo năm, tháng, kỳ)
export const isFixedUnit = (collectBy?: number) => {
    return !collectBy || collectBy === 1 || collectBy === 2 || collectBy === 4;
};

// Helper: Xây dựng danh sách items đầy đủ dựa trên toàn bộ incomeSources của trường
const buildLevelItems = (
    existingItems: QuickRegisterItem[] | undefined,
    sources: IncomeSchool[]
): QuickRegisterItem[] => {
    const existingMap = new Map<number, QuickRegisterItem>();
    if (existingItems && existingItems.length > 0) {
        existingItems.forEach((it) => {
            if (it.incomeSchoolId) {
                existingMap.set(it.incomeSchoolId, it);
            }
        });
    }

    return sources.map((inc) => {
        const exist = existingMap.get(inc.id);
        const cBy = (inc as any)?.collectBy || (inc as any)?.collect_by || exist?.collectBy || 1;
        const isFixed = isFixedUnit(cBy);
        if (exist) {
            const units = isFixed ? 1 : (exist.units ?? 1);
            return {
                ...exist,
                incomeSchoolId: inc.id,
                incomeSchoolName: inc.name,
                incomeSchoolCode: inc.code,
                collectBy: cBy,
                units: units,
                unitPrice: exist.unitPrice ?? 0,
                amount: units * (exist.unitPrice ?? 0),
                isDefault: exist.isDefault !== undefined ? Boolean(exist.isDefault) : true
            };
        }
        return {
            incomeSchoolId: inc.id,
            incomeSchoolName: inc.name,
            incomeSchoolCode: inc.code,
            collectBy: cBy,
            units: 1,
            unitPrice: 0,
            amount: 0,
            isDefault: false
        };
    });
};

const QuickRegisterEditor: React.FC<QuickRegisterEditorProps> = ({
    initialData,
    revenueBlockId,
    blockName,
    month,
    year,
    incomeSources,
    blocks = [],
    onBack,
    onSaveSuccess
}) => {
    // Basic Info State
    const [name, setName] = useState<string>(
        initialData?.name || `Phiếu thu ${blockName} T${month}/${year}`
    );

    // Levels & Items State
    const [levels, setLevels] = useState<QuickRegisterLevel[]>(() => {
        if (initialData?.levels && initialData.levels.length > 0) {
            const firstLvl = initialData.levels[0];
            return [
                {
                    ...firstLvl,
                    items: buildLevelItems(firstLvl.items, incomeSources)
                }
            ];
        }
        return [
            {
                name: 'Mức 1',
                items: buildLevelItems([], incomeSources)
            }
        ];
    });

    const [activeTabKey, setActiveTabKey] = useState<string>('0');
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [loadingPrevious, setLoadingPrevious] = useState<boolean>(false);
    const [searchText, setSearchText] = useState<string>('');
    const [hasAttemptedSave, setHasAttemptedSave] = useState<boolean>(false);

    // Modal Copy From Other Register
    const [copyModalVisible, setCopyModalVisible] = useState<boolean>(false);
    const [sourceBlockList, setSourceBlockList] = useState<RevenueBlockOption[]>(blocks);
    const [copyBlockId, setCopyBlockId] = useState<number>(revenueBlockId);
    const [copyMonth, setCopyMonth] = useState<number>(month === 1 ? 12 : month - 1);
    const [copyYear, setCopyYear] = useState<number>(month === 1 ? year - 1 : year);
    const [loadingSource, setLoadingSource] = useState<boolean>(false);
    const [sourceRegister, setSourceRegister] = useState<QuickRegister | null>(null);
    const [hasSearched, setHasSearched] = useState<boolean>(false);

    // Fetch blocks nếu chưa truyền từ props
    useEffect(() => {
        if (blocks && blocks.length > 0) {
            setSourceBlockList(blocks);
            return;
        }
        const schoolId = localStorage.getItem('schoolId');
        if (!schoolId) return;
        getRequest(`/cms/accountant/revenue-blocks?schoolId=${schoolId}&status=1`).then((res) => {
            if (res?.code === 200 && Array.isArray(res.info)) {
                setSourceBlockList(res.info);
            }
        });
    }, [blocks]);

    // Đảm bảo khi incomeSources load muộn thì cập nhật đủ vào levels (chỉ chạy 1 lần khi có nguồn thu)
    const hasInitializedSources = useRef(false);
    useEffect(() => {
        if (!incomeSources || incomeSources.length === 0) return;
        if (hasInitializedSources.current) return;
        hasInitializedSources.current = true;

        setLevels((prevLevels) => {
            return prevLevels.map((lvl) => ({
                ...lvl,
                items: buildLevelItems(lvl.items, incomeSources)
            }));
        });
    }, [incomeSources]);

    const activeIdx = Math.max(0, Math.min(Number(activeTabKey) || 0, levels.length - 1));
    const currentLevel = levels[activeIdx] || levels[0];

    // Helper: Format currency
    const formatCurrency = (val?: number) => {
        return (val || 0).toLocaleString('vi-VN') + ' đ';
    };

    // Tính tổng mức thu chuẩn của Level (CHỈ tính các khoản có isDefault == true)
    const calcLevelTotal = (lvl?: QuickRegisterLevel) => {
        if (!lvl || !lvl.items) return 0;
        return lvl.items
            .filter((it) => it.isDefault)
            .reduce((sum, it) => sum + (it.units || 1) * (it.unitPrice || 0), 0);
    };

    // --- Actions on Levels ---
    const handleAddLevel = () => {
        const newLevelNumber = levels.length + 1;
        const newLevel: QuickRegisterLevel = {
            name: `Mức ${newLevelNumber}`,
            items: buildLevelItems([], incomeSources)
        };
        const nextLevels = [...levels, newLevel];
        setLevels(nextLevels);
        setActiveTabKey(String(nextLevels.length - 1));
        toast.info(`Đã thêm Mức ${newLevelNumber}`);
    };

    const handleDuplicateLevel = (sourceIdx: number) => {
        const sourceLevel = levels[sourceIdx];
        if (!sourceLevel) return;

        const duplicatedLevel: QuickRegisterLevel = {
            name: `${sourceLevel.name} (Bản sao)`,
            items: (sourceLevel.items || []).map((it) => ({
                ...it,
                id: undefined,
                quickRegisterLevelId: undefined
            }))
        };

        const nextLevels = [...levels, duplicatedLevel];
        setLevels(nextLevels);
        setActiveTabKey(String(nextLevels.length - 1));
        toast.success(`Đã nhân bản "${sourceLevel.name}" thành công!`);
    };

    const handleRemoveLevel = (targetIdx: number) => {
        if (levels.length <= 1) {
            toast.warn('Phiếu thu phải có ít nhất 1 mức thu!');
            return;
        }

        const removedName = levels[targetIdx]?.name;
        const nextLevels = levels.filter((_, idx) => idx !== targetIdx);
        setLevels(nextLevels);

        const nextIdx = Math.min(activeIdx, nextLevels.length - 1);
        setActiveTabKey(String(nextIdx));
        toast.info(`Đã xóa "${removedName}"`);
    };

    const handleUpdateLevelName = (idx: number, newName: string) => {
        setLevels((prevLevels) =>
            prevLevels.map((lvl, lIdx) => (lIdx === idx ? { ...lvl, name: newName } : lvl))
        );
    };

    // --- Actions on Items in Table ---
    const handleUpdateItem = (
        incomeSchoolId: number,
        field: 'units' | 'unitPrice' | 'isDefault',
        value: any
    ) => {
        setLevels((prevLevels) =>
            prevLevels.map((lvl, lIdx) => {
                if (lIdx !== activeIdx || !lvl.items) return lvl;

                const newItems = lvl.items.map((it) => {
                    if (it.incomeSchoolId !== incomeSchoolId) return it;
                    const updated: QuickRegisterItem = {
                        ...it,
                        [field]: value
                    };
                    const inc = incomeSources.find((i) => i.id === incomeSchoolId);
                    const cBy = it.collectBy || (inc as any)?.collectBy || (inc as any)?.collect_by || 1;
                    const isFixed = isFixedUnit(cBy);

                    const rawUnits = field === 'units' ? (Number(value) || 1) : (updated.units || 1);
                    const u = isFixed ? 1 : rawUnits;
                    const p = field === 'unitPrice' ? (Number(value) || 0) : (updated.unitPrice || 0);

                    updated.units = u;
                    updated.unitPrice = p;
                    updated.amount = u * p;
                    return updated;
                });

                return {
                    ...lvl,
                    items: newItems
                };
            })
        );
    };

    // Toggle tất cả khoản làm mặc định hoặc bỏ chọn tất cả (nếu đang lọc tìm kiếm thì chỉ áp dụng cho danh sách hiển thị)
    const handleToggleAllDefaults = (checked: boolean) => {
        const targetIds = new Set(filteredItems.map((f) => f.incomeSchoolId));
        setLevels((prevLevels) =>
            prevLevels.map((lvl, lIdx) => {
                if (lIdx !== activeIdx || !lvl.items) return lvl;
                return {
                    ...lvl,
                    items: lvl.items.map((it) => {
                        if (targetIds.has(it.incomeSchoolId)) {
                            return {
                                ...it,
                                isDefault: checked
                            };
                        }
                        return it;
                    })
                };
            })
        );
    };

    // --- Tìm kiếm cấu hình nguồn thu nguồn để sao chép ---
    const handleFindSourceRegister = async (bId = copyBlockId, m = copyMonth, y = copyYear) => {
        const schoolId = localStorage.getItem('schoolId');
        if (!schoolId || !bId) return;

        setLoadingSource(true);
        setHasSearched(true);
        try {
            const res = await getRequest(
                `/cms/accountant/quick-registers?schoolId=${schoolId}&month=${m}&year=${y}&revenueBlockId=${bId}`
            );
            if (res?.code === 200 && Array.isArray(res.info) && res.info.length > 0) {
                setSourceRegister(res.info[0]);
            } else {
                setSourceRegister(null);
            }
        } catch (err) {
            toast.error('Lỗi khi tải cấu hình nguồn!');
            setSourceRegister(null);
        } finally {
            setLoadingSource(false);
        }
    };

    // --- Áp dụng cấu hình nguồn thu đã copy vào Editor ---
    const handleApplyCopiedRegister = () => {
        if (!sourceRegister || !sourceRegister.levels || sourceRegister.levels.length === 0) {
            toast.warn('Phiếu nguồn không có mức thu nào để sao chép!');
            return;
        }

        const newLevels = sourceRegister.levels.slice(0, 1).map((lvl) => ({
            name: lvl.name || 'Mức 1',
            sortOrder: 0,
            items: buildLevelItems(lvl.items, incomeSources)
        }));

        setLevels(newLevels);
        setActiveTabKey('0');
        setHasAttemptedSave(false);
        const bName = sourceBlockList.find((b) => b.id === copyBlockId)?.name || 'Khối thu';
        toast.success(`Đã sao chép thành công cấu hình từ ${bName} Tháng ${copyMonth}/${copyYear}!`);
        setCopyModalVisible(false);
    };

    // --- Load Previous Month Config ---
    const handleLoadPreviousMonth = async () => {
        const schoolId = localStorage.getItem('schoolId');
        if (!schoolId) return;

        const prevMonth = month === 1 ? 12 : month - 1;
        const prevYear = month === 1 ? year - 1 : year;

        setLoadingPrevious(true);
        try {
            const res = await getRequest(
                `/cms/accountant/quick-registers?schoolId=${schoolId}&month=${prevMonth}&year=${prevYear}&revenueBlockId=${revenueBlockId}`
            );
            if (res?.code === 200 && Array.isArray(res.info) && res.info.length > 0) {
                const prevQr: QuickRegister = res.info[0];
                if (prevQr.levels && prevQr.levels.length > 0) {
                    setLevels(
                        prevQr.levels.slice(0, 1).map((lvl) => ({
                            name: lvl.name || 'Mức 1',
                            sortOrder: 0,
                            items: buildLevelItems(lvl.items, incomeSources)
                        }))
                    );
                    setActiveTabKey('0');
                    toast.success(`Đã tải cấu hình từ Tháng ${prevMonth}/${prevYear}!`);
                }
            } else {
                toast.warn(`Không tìm thấy phiếu đăng ký nhanh nào ở Tháng ${prevMonth}/${prevYear}!`);
            }
        } catch (err) {
            toast.error('Lỗi khi tải cấu hình tháng trước!');
        } finally {
            setLoadingPrevious(false);
        }
    };

    // --- Submit Form ---
    const handleSave = async () => {
        setHasAttemptedSave(true);
        const schoolId = localStorage.getItem('schoolId');
        if (!schoolId) {
            toast.error('Thiếu thông tin trường học!');
            return;
        }

        if (!name.trim()) {
            toast.warn('Vui lòng nhập tên phiếu thu!');
            return;
        }

        // Kiểm tra hợp lệ các mức thu:
        // 1. Mỗi mức phải có ít nhất 1 khoản được tích chọn
        // 2. Validate TẤT CẢ các nguồn thu: đơn giá phải > 0 (không phân biệt đã tích hay chưa)
        for (let l = 0; l < levels.length; l++) {
            const lvl = levels[l];
            const defaultItems = (lvl.items || []).filter((it) => it.isDefault);
            if (defaultItems.length === 0) {
                toast.warn(`"${lvl.name}" phải có ít nhất 1 khoản thu được chọn!`);
                setActiveTabKey(String(l));
                return;
            }

            for (const it of (lvl.items || [])) {
                if (!it.unitPrice || it.unitPrice <= 0) {
                    const incName = it.incomeSchoolName || incomeSources.find((i) => i.id === it.incomeSchoolId)?.name || 'Khoản thu';
                    toast.warn(`Khoản thu "${incName}" trong "${lvl.name}" có đơn giá bằng 0. Vui lòng nhập đơn giá hợp lệ (> 0)!`);
                    setActiveTabKey(String(l));
                    setSearchText('');
                    return;
                }
            }
        }

        const payload = {
            schoolId: Number(schoolId),
            revenueBlockId,
            month,
            year,
            name: name.trim(),
            levels: levels.map((l, idx) => {
                const items = (l.items || []).map((it) => {
                    const inc = incomeSources.find((i) => i.id === it.incomeSchoolId);
                    const cBy = it.collectBy || (inc as any)?.collectBy || (inc as any)?.collect_by || 1;
                    const isFixed = isFixedUnit(cBy);
                    const units = isFixed ? 1 : (it.units || 1);
                    const unitPrice = it.unitPrice || 0;
                    return {
                        incomeSchoolId: it.incomeSchoolId,
                        units,
                        unitPrice,
                        amount: units * unitPrice,
                        isDefault: Boolean(it.isDefault)
                    };
                });
                const totalAmount = items
                    .filter((it) => it.isDefault)
                    .reduce((sum, item) => sum + item.amount, 0);
                return {
                    name: l.name.trim(),
                    sortOrder: idx,
                    totalAmount,
                    items
                };
            })
        };

        setSubmitting(true);
        try {
            let res;
            if (initialData?.id) {
                res = await putRequest(`/cms/accountant/quick-registers/${initialData.id}?schoolId=${schoolId}`, payload);
            } else {
                res = await postRequest(`/cms/accountant/quick-registers?schoolId=${schoolId}`, payload);
            }

            if (res?.code === 200) {
                toast.success(initialData ? 'Cập nhật phiếu thành công!' : 'Tạo phiếu thành công!');
                onSaveSuccess();
            } else {
                toast.error(res?.msg || 'Lưu phiếu thất bại!');
            }
        } catch (err: any) {
            toast.error(err?.message || 'Có lỗi xảy ra khi lưu phiếu!');
        } finally {
            setSubmitting(false);
        }
    };

    // Danh sách items của Level đang chọn
    const currentLevelItems = useMemo(() => currentLevel?.items || [], [currentLevel]);
    const defaultCount = useMemo(() => currentLevelItems.filter((it) => it.isDefault).length, [currentLevelItems]);

    // Filtered items khi tìm kiếm nhanh theo mã hoặc tên
    const filteredItems = useMemo(() => {
        if (!searchText.trim()) return currentLevelItems;
        const q = searchText.trim().toLowerCase();
        return currentLevelItems.filter((item) => {
            const nameMatch = item.incomeSchoolName?.toLowerCase().includes(q);
            const codeMatch = item.incomeSchoolCode?.toLowerCase().includes(q);
            return nameMatch || codeMatch;
        });
    }, [currentLevelItems, searchText]);

    const allChecked = filteredItems.length > 0 && filteredItems.every((it) => it.isDefault);
    const someChecked = filteredItems.some((it) => it.isDefault);
    const isIndeterminate = someChecked && !allChecked;

    return (
        <div style={{ padding: '4px 0' }}>
            {/* Header tiêu đề màn hình & thao tác chính căn đều trên 1 hàng */}
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                <Space size="middle" align="center" wrap>
                    <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
                        Quay lại
                    </Button>
                    <span style={{ fontSize: 16, fontWeight: 600 }}>
                        {initialData ? 'Chỉnh sửa phiếu đăng ký nhanh' : 'Lập phiếu đăng ký nhanh'}
                    </span>
                    <Tag color="blue" style={{ margin: 0, fontWeight: 600 }}>
                        {blockName}
                    </Tag>
                    <Tag color="purple" style={{ margin: 0, fontWeight: 600 }}>
                        Tháng {month} / {year}
                    </Tag>
                </Space>

                <Space align="center" wrap>
                    <Button
                        icon={<CopyOutlined />}
                        onClick={() => {
                            setCopyModalVisible(true);
                            handleFindSourceRegister(copyBlockId, copyMonth, copyYear);
                        }}
                    >
                        Sao chép từ nguồn khác
                    </Button>
                    <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        loading={submitting}
                        onClick={handleSave}
                    >
                        Lưu phiếu thu
                    </Button>
                </Space>
            </div>

            {/* Khung nội dung cấu hình chính */}
            <Card>
                {/* Dòng Tên phiếu thu */}
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                    <Space align="center" size={12} style={{ flex: 1, maxWidth: 560 }}>
                        <span style={{ fontWeight: 500, color: '#595959', whiteSpace: 'nowrap' }}>
                            <span style={{ color: '#ff4d4f' }}>*</span> Tên phiếu thu:
                        </span>
                        <Input
                            status={hasAttemptedSave && !name.trim() ? 'error' : undefined}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Ví dụ: Phiếu thu Bán trú Tháng 10/2026"
                            style={{ width: 380 }}
                        />
                    </Space>
                    <Input
                        placeholder="Tìm theo mã hoặc tên nguồn thu..."
                        prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        allowClear
                        style={{ width: 280 }}
                    />
                </div>

                {/* Nội dung chi tiết nguồn thu của khối thu */}
                <div style={{ marginTop: 12 }}>

                    {/* Bảng chi tiết toàn bộ nguồn thu tinh gọn */}
                    <Table<QuickRegisterItem>
                        rowKey="incomeSchoolId"
                        dataSource={filteredItems}
                        pagination={false}
                        bordered
                        size="middle"
                        rowClassName={(record) => (record.isDefault ? 'ant-table-row-selected' : '')}
                        columns={[
                            {
                                title: (
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                        <Checkbox
                                            indeterminate={isIndeterminate}
                                            checked={allChecked}
                                            onChange={(e) => handleToggleAllDefaults(e.target.checked)}
                                        />
                                    </div>
                                ),
                                key: 'isDefault',
                                dataIndex: 'isDefault',
                                width: 55,
                                align: 'center',
                                render: (isDef: boolean | undefined, record: QuickRegisterItem) => (
                                    <Checkbox
                                        checked={Boolean(isDef)}
                                        onChange={(e) =>
                                            handleUpdateItem(record.incomeSchoolId, 'isDefault', e.target.checked)
                                        }
                                    />
                                )
                            },
                            {
                                title: 'Mã khoản',
                                dataIndex: 'incomeSchoolCode',
                                key: 'incomeSchoolCode',
                                width: 120,
                                render: (val) => (val ? <Tag>{val}</Tag> : <span style={{ color: '#bfbfbf' }}>-</span>)
                            },
                            {
                                title: 'Tên nguồn thu',
                                dataIndex: 'incomeSchoolName',
                                key: 'incomeSchoolName',
                                render: (val, record) => (
                                    <span style={{ fontWeight: record.isDefault ? 600 : 400 }}>
                                        {val}
                                    </span>
                                )
                            },
                            {
                                title: 'Hình thức',
                                dataIndex: 'collectBy',
                                key: 'collectBy',
                                width: 120,
                                align: 'center',
                                render: (val, record) => {
                                    const inc = incomeSources.find((i) => i.id === record.incomeSchoolId);
                                    const cBy = val || record.collectBy || (inc as any)?.collectBy || (inc as any)?.collect_by || 1;
                                    const tag = COLLECT_BY_LABEL[cBy] || { text: 'Theo tháng', color: 'blue' };
                                    return <Tag color={tag.color}>{tag.text}</Tag>;
                                }
                            },
                            {
                                title: 'Số lượng / Suất',
                                dataIndex: 'units',
                                key: 'units',
                                width: 130,
                                align: 'center',
                                render: (val, record) => {
                                    const inc = incomeSources.find((i) => i.id === record.incomeSchoolId);
                                    const cBy = record.collectBy || (inc as any)?.collectBy || (inc as any)?.collect_by || 1;
                                    const isFixed = isFixedUnit(cBy);
                                    return (
                                        <InputNumber
                                            min={1}
                                            max={31}
                                            precision={0}
                                            value={isFixed ? 1 : (val || 1)}
                                            disabled={isFixed}
                                            onChange={(v) =>
                                                handleUpdateItem(record.incomeSchoolId, 'units', v || 1)
                                            }
                                            title={isFixed ? 'Khoản thu theo Năm / Tháng / Kỳ có số lượng cố định là 1' : undefined}
                                            style={{ width: '100%' }}
                                        />
                                    );
                                }
                            },
                            {
                                title: 'Đơn giá (đ)',
                                dataIndex: 'unitPrice',
                                key: 'unitPrice',
                                width: 170,
                                align: 'right',
                                render: (val, record) => {
                                    const isInvalid = hasAttemptedSave && (!val || val <= 0);
                                    return (
                                        <div>
                                            <InputNumber
                                                status={isInvalid ? 'error' : undefined}
                                                min={0}
                                                step={10000}
                                                value={val ?? 0}
                                                onChange={(v) =>
                                                    handleUpdateItem(record.incomeSchoolId, 'unitPrice', v ?? 0)
                                                }
                                                formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                parser={(v) => (v ? v.replace(/\$\s?|(,*)/g, '') : '') as any}
                                                placeholder="0"
                                                style={{ width: '100%' }}
                                            />
                                            {isInvalid && (
                                                <div style={{ color: '#ff4d4f', fontSize: 11, textAlign: 'left', marginTop: 2 }}>
                                                    * Bắt buộc &gt; 0
                                                </div>
                                            )}
                                        </div>
                                    );
                                }
                            },
                            {
                                title: 'Thành tiền (đ)',
                                key: 'amount',
                                width: 170,
                                align: 'right',
                                render: (_, record) => {
                                    const amount = (record.units || 1) * (record.unitPrice || 0);
                                    if (record.isDefault) {
                                        return <b style={{ color: '#1677ff' }}>{formatCurrency(amount)}</b>;
                                    }
                                    return <span style={{ color: '#bfbfbf' }}>0 đ</span>;
                                }
                            }
                        ]}
                        summary={() => {
                            const total = calcLevelTotal(currentLevel);
                            return (
                                <Table.Summary.Row style={{ backgroundColor: '#fafafa' }}>
                                    <Table.Summary.Cell index={0} colSpan={6}>
                                        <Space size={8} align="center">
                                            <span style={{ fontWeight: 600 }}>Tổng cộng:</span>
                                            <Tag color="blue" style={{ margin: 0 }}>
                                                {defaultCount} khoản
                                            </Tag>
                                        </Space>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={1} align="right">
                                        <span style={{ fontWeight: 700, fontSize: 15, color: '#f5222d' }}>
                                            {formatCurrency(total)}
                                        </span>
                                    </Table.Summary.Cell>
                                </Table.Summary.Row>
                            );
                        }}
                    />
                    {/* Thao tác cuối form */}
                    <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                        <Space>
                            <Button onClick={onBack}>Hủy</Button>
                            <Button
                                type="primary"
                                icon={<SaveOutlined />}
                                loading={submitting}
                                onClick={handleSave}
                            >
                                Lưu phiếu thu
                            </Button>
                        </Space>
                    </div>
                </div>
            </Card>

            {/* Modal: Sao chép từ nguồn thu khác */}
            <Modal
                title={
                    <Space>
                        <CopyOutlined style={{ color: '#1677ff' }} />
                        <span style={{ fontWeight: 600 }}>Sao chép cấu hình từ nguồn khác</span>
                    </Space>
                }
                open={copyModalVisible}
                onCancel={() => setCopyModalVisible(false)}
                width={650}
                footer={[
                    <Button key="cancel" onClick={() => setCopyModalVisible(false)}>
                        Đóng
                    </Button>,
                    <Button
                        key="apply"
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        disabled={!sourceRegister}
                        onClick={handleApplyCopiedRegister}
                    >
                        Áp dụng vào phiếu
                    </Button>
                ]}
            >
                <div style={{ marginBottom: 16 }}>
                    <Row gutter={12} align="bottom">
                        <Col span={10}>
                            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Khối thu nguồn:</div>
                            <Select
                                value={copyBlockId}
                                onChange={(val) => {
                                    setCopyBlockId(val);
                                    setSourceRegister(null);
                                    setHasSearched(false);
                                }}
                                style={{ width: '100%' }}
                                placeholder="Chọn khối thu nguồn"
                            >
                                {sourceBlockList.map((b) => (
                                    <Option key={b.id} value={b.id}>
                                        {b.name} ({b.code})
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                        <Col span={5}>
                            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Tháng:</div>
                            <Select
                                value={copyMonth}
                                onChange={(val) => {
                                    setCopyMonth(val);
                                    setSourceRegister(null);
                                    setHasSearched(false);
                                }}
                                style={{ width: '100%' }}
                            >
                                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                                    <Option key={m} value={m}>
                                        Tháng {m}
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                        <Col span={5}>
                            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Năm:</div>
                            <InputNumber
                                min={2020}
                                max={2035}
                                value={copyYear}
                                onChange={(val) => {
                                    if (val) {
                                        setCopyYear(val);
                                        setSourceRegister(null);
                                        setHasSearched(false);
                                    }
                                }}
                                style={{ width: '100%' }}
                            />
                        </Col>
                        <Col span={4}>
                            <Button
                                type="primary"
                                icon={<SearchOutlined />}
                                loading={loadingSource}
                                onClick={() => handleFindSourceRegister(copyBlockId, copyMonth, copyYear)}
                                style={{ width: '100%' }}
                            >
                                Tìm
                            </Button>
                        </Col>
                    </Row>
                </div>

                <Divider style={{ margin: '16px 0' }} />

                <Spin spinning={loadingSource}>
                    {sourceRegister ? (
                        <div style={{ background: '#fafafa', border: '1px solid #d9d9d9', borderRadius: 6, padding: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                <span style={{ fontWeight: 600 }}>{sourceRegister.name}</span>
                                <Tag color="blue">Tháng {sourceRegister.month}/{sourceRegister.year}</Tag>
                            </div>
                            <div style={{ color: '#595959', fontSize: 13 }}>
                                Tổng mức thu chuẩn:{' '}
                                <b style={{ color: '#1677ff' }}>{formatCurrency(sourceRegister.levels?.[0]?.totalAmount)}</b> —{' '}
                                <b>{(sourceRegister.levels?.[0]?.items || []).filter((i) => i.isDefault !== false).length}</b> khoản thu mặc định
                            </div>
                        </div>
                    ) : hasSearched ? (
                        <Empty
                            description="Không tìm thấy phiếu đăng ký nhanh nào trong tháng/khối này"
                            style={{ margin: '16px 0' }}
                        />
                    ) : (
                        <div style={{ textAlign: 'center', color: '#8c8c8c', padding: '16px 0' }}>
                            Chọn khối, kỳ nguồn và bấm "Tìm" để kiểm tra cấu hình
                        </div>
                    )}
                </Spin>
            </Modal>
        </div>
    );
};

export default QuickRegisterEditor;
